import type { PublicBoost } from '~/domains/contest/domains/services/interfaces/propsService/api/boosts';

import type { PromotionItem } from '../services/promotions.interface';
import type { VoucherResponseDto } from '../types/voucher.types';
import type {
  BonusDefinition,
  NormalizedBonus,
} from '../types/bonus.types';
import { PromoType } from '../types/enums';
import type { PromoCardProps } from '../types/carousel.types';

import {
  formatBoostValue,
  formatBoostCategoryToDisplay,
  formatVoucherValue,
  normalizeBonus,
  formatBonusAmountDisplay,
} from './formatters';

/**
 * Filters out inactive bonuses and normalizes the rest.
 */
export const normalizeBonuses = (bonuses: BonusDefinition[]): NormalizedBonus[] =>
  bonuses.filter((bonus) => bonus.isActive).map(normalizeBonus);


interface ParsePromotionItemsToPromoCardPropsProps {
  items: PromotionItem[];
  selectedPromoId: string | null;
  handleBoostApply: (boost: PublicBoost) => void;
  handleVoucherApply: (voucher: VoucherResponseDto) => void;
  handleViewBoostTerms: (boost: PublicBoost) => void;
  handleViewVoucherDetails: (voucher: VoucherResponseDto) => void;
  handleDepositBonusAction: (bonus: BonusDefinition) => void;
  handleViewDepositBonusDetails: (bonus: BonusDefinition) => void;
}

export const parseDepositBonusToCardProps = (
  bonus: BonusDefinition,
  isSelected?: boolean,
): PromoCardProps => ({
    type: PromoType.DEPOSIT_BONUS,
    id: bonus.id,
    title: bonus.title,
    description: bonus.description,
    expirationDate: bonus.assignmentExpiresAt,
    value: formatBonusAmountDisplay(bonus),
    isSelected: isSelected ?? false,
  });

export const parseVoucherToCardProps = (
  voucher: VoucherResponseDto,
  isSelected?: boolean,
): PromoCardProps => ({
  type: PromoType.VOUCHER,
  id: voucher.id,
  title: voucher.title,
  description: voucher.description,
  expirationDate: voucher.expiresAt,
  voucherType: voucher.voucherType,
  restrictedContestId: voucher.restrictedContestId,
  value: formatVoucherValue(voucher.maxValue),
  isSelected: isSelected ?? false,
  });

export const parseBoostToCardProps = (
  boost: PublicBoost,
  isSelected?: boolean,
): PromoCardProps => ({
  type: PromoType.BOOST,
  id: boost.id,
  title: boost.title,
  description: boost.description,
  expirationDate: boost.expires_at instanceof Date
    ? boost.expires_at.toISOString()
    : boost.expires_at,
  boostType: formatBoostCategoryToDisplay(boost.type),
  value: formatBoostValue(boost),
  isSelected: isSelected ?? false,
});

export const constructPromoCardProps =
  <T extends BonusDefinition | VoucherResponseDto | PublicBoost>(
  item: T,
  selectedPromoId: string | null,
  parser: (item: T) => PromoCardProps,
  handleViewDetails: (item: T) => void,
  handleApply: (item: T) => void,
): PromoCardProps => ({
  ...parser(item),
  isSelected: item.id === selectedPromoId,
  onActionLeft: () => handleViewDetails(item),
  onActionRight: () => handleApply(item),
});

export const parsePromotionItemsToPromoCardProps = ({
  items,
  selectedPromoId,
  handleBoostApply,
  handleVoucherApply,
  handleViewBoostTerms,
  handleViewVoucherDetails,
  handleDepositBonusAction,
  handleViewDepositBonusDetails,
}: ParsePromotionItemsToPromoCardPropsProps): PromoCardProps[] | null => items?.map((item) => {
    switch (item.type) {
      case PromoType.DEPOSIT_BONUS: {
        return constructPromoCardProps(
          item.bonus,
          selectedPromoId,
          parseDepositBonusToCardProps,
          handleViewDepositBonusDetails,
          handleDepositBonusAction,
        );
      }
      case PromoType.VOUCHER:
        return constructPromoCardProps(
          item.voucher,
          selectedPromoId,
          parseVoucherToCardProps,
          handleViewVoucherDetails,
          handleVoucherApply,
        );
      case PromoType.BOOST:
        return constructPromoCardProps(
          item.boost,
          selectedPromoId,
          parseBoostToCardProps,
          handleViewBoostTerms,
          handleBoostApply,
        );
      default:
        return null;
    }
  });
